// Apolo IDE — context-usage breakdown for the composer drop-up (Cursor-style).
//
// HONEST SCOPE: this measures the CONVERSATION footprint that the renderer
// actually holds — user/agent turns, tool calls, and any system/error notices —
// against the model's context window. It does NOT include the assembled system
// prompt (skills / tool definitions / brain injection), because those are built
// at send-time inside the agent loop and never reach the renderer as messages.
// So treat this as "how much of the window the live conversation is using",
// not Cursor's full prompt-assembly split.

import { approxTokens } from './costEstimator.js';
import { findModel } from './modelRegistry.js';

// role → category. Apolo roles: user, agent, tool, system, error.
const CATEGORIES = [
  { key: 'conversation', label: 'Conversa', roles: ['user', 'agent', 'assistant'], color: '#5b8a5e' },
  { key: 'tools', label: 'Ferramentas', roles: ['tool'], color: '#c08532' },
  { key: 'system', label: 'Sistema', roles: ['system', 'error'], color: '#9b7fe8' },
];

function categoryFor(role) {
  return CATEGORIES.find((c) => c.roles.includes(role)) || CATEGORIES[2];
}

/**
 * @param {Array<{role:string, content?:string}>} messages
 * @param {string} model
 * @returns {{ used:number, limit:number, pct:(number|null),
 *   segments: Array<{key:string,label:string,tokens:number,color:string}> }}
 */
export function computeContextUsage(messages, model) {
  const reg = model ? findModel(model) : null;
  const limit = reg?.contextWindow || 0;

  const totals = new Map(CATEGORIES.map((c) => [c.key, 0]));
  for (const m of Array.isArray(messages) ? messages : []) {
    const text = typeof m?.content === 'string' ? m.content : '';
    if (!text) continue;
    const cat = categoryFor(m?.role);
    totals.set(cat.key, totals.get(cat.key) + approxTokens(text));
  }

  const segments = CATEGORIES
    .map((c) => ({ key: c.key, label: c.label, tokens: totals.get(c.key) || 0, color: c.color }))
    .filter((s) => s.tokens > 0);

  const used = segments.reduce((s, x) => s + x.tokens, 0);
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : null;
  return { used, limit, pct, segments };
}
